import type { GridTableColumnDef } from '@trukt/tables/GridTable';
import type { ColumnGroups, ColumnsSettingsCardProps } from '@trukt/ui/columns-settings-card';

import { CommodityCondition } from '@trukt/ui/CommodityCondition';
import { OrderJobStatusPill } from '@trukt/ui/OrderJobStatusPill';
import { Tooltip } from '@trukt/ui/Tooltip';
import { Typography } from '@trukt/ui/Typography';

import { GetOrdersSortableFieldLiterals } from '@api';
import { OrderStatusLiterals } from '@trukt/types/Order';

import { LabelGroup } from './components';
import {
  OrderTableFields,
  OrderTableColumnLiterals,
  OrderReportColumnLiterals,
  SORTABLE_FIELD_LITERALS,
  DateRange,
} from './OrderListView.types';
import { ISODate } from '@trukt/types/ISODate';
import { FormatDateOptions, formatDateRange } from '@trukt/portal-utils/formatDate';

export enum COMPOUND_FILTER_KEYS {
  STATUS = 'status',
  DATES = 'dates',
  PICK_UP = 'pickup',
  DELIVERY = 'delivery',
  BRANCH = 'branch',
  CONTACT = 'contact',
}

export const COLUMN_GROUPS: ColumnGroups = [
  {
    label: 'Order Details',
    columns: [
      'contactName',
      'status',
      'poNumber',
      'client',
      'actualRevenue',
      'commodity',
      'vin',
      'lotNumber',
    ],
  },
  {
    label: 'Dates',
    columns: [
      'dateCreated',
      'requestedPickupDate',
      'scheduledPickupDate',
      'pickupCompletedDate',
      'requestedDeliveryDate',
      'scheduledDeliveryDate',
      'deliveryCompletedDate',
    ],
  },
  {
    label: 'Pickup Details',
    columns: ['pickupTerminal', 'pickupAddress'],
  },
  {
    label: 'Delivery Details',
    columns: ['deliveryTerminal', 'deliveryAddress'],
  },
];

export const COLUMN_DICTIONARY: ColumnsSettingsCardProps['dictionary'] = {
  contactName: 'Contact Name',
  status: 'Status',
  dateCreated: 'Date Created',
  poNumber: 'PO Number',
  client: 'Branch (Client)',
  actualRevenue: 'Cost',
  commodity: 'Commodity',
  vin: 'VIN',
  lotNumber: 'Lot Number',
  pickupTerminal: 'Pickup Terminal',
  pickupAddress: 'Pickup Location',
  requestedPickupDate: 'Date Requested Pickup',
  scheduledPickupDate: 'Date Scheduled Pickup',
  pickupCompletedDate: 'Date Picked Up On',
  deliveryTerminal: 'Delivery Terminal',
  deliveryAddress: 'Delivery Location',
  requestedDeliveryDate: 'Date Requested Delivery',
  scheduledDeliveryDate: 'Date Scheduled Delivery',
  deliveryCompletedDate: 'Date Delivered On',
};

const FORMAT_TOOLTIP_DATE: FormatDateOptions = 'Sun 12/31, 12:00 AM';
const FORMAT_VALUE_DATE: FormatDateOptions = '12/31/1899, 12:00 AM';

export const ORDER_TABLE_LITERAL_COLUMNS: OrderTableColumnLiterals[] = [
  'number',
  'status',
  'client',
  'dateCreated',
  'contactName',
  'poNumber',
  'actualRevenue',
  'commodity',
  'vin',
  'lotNumber',
  'pickupTerminal',
  'pickupAddress',
  'deliveryTerminal',
  'deliveryAddress',
  'requestedPickupDate',
  'scheduledPickupDate',
  'pickupCompletedDate',
  'requestedDeliveryDate',
  'scheduledDeliveryDate',
  'deliveryCompletedDate',
  'dateUpdated',
];

type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>;

export const TABLE_COLUMNS_TO_REPORT_COLUMNS: PartialRecord<
  OrderTableColumnLiterals,
  OrderReportColumnLiterals[]
> = {
  status: ['status'],
  number: ['orderId'],
  dateCreated: ['createdDate'],
  contactName: ['contactName'],
  client: ['branch'],
  poNumber: ['poNumber'],
  actualRevenue: ['customerTotalTariff'],
  commodity: ['vehicleInfo', 'inoperable'],
  lotNumber: ['lotNumber'],
  pickupTerminal: ['pickupBusinessName', 'pickupNotes'],
  pickupAddress: ['pickupAddress', 'pickupCity', 'pickupState', 'pickupZip'],
  deliveryAddress: ['deliveryAddress', 'deliveryCity', 'deliveryState', 'deliveryZip'],
  deliveryTerminal: ['deliveryBusinessName', 'deliveryNotes'],
  requestedPickupDate: ['pickupRequestedDate'],
  scheduledPickupDate: ['pickupScheduledDate'],
  pickupCompletedDate: ['pickupCompletedDate'],
  requestedDeliveryDate: ['deliveryRequestedDate'],
  scheduledDeliveryDate: ['deliveryScheduledDate'],
  deliveryCompletedDate: ['deliveryCompletedDate'],
  vin: ['vin'],
  dateUpdated: ['updatedAt'],
};

export const REPORT_COLUMNS_TO_TABLE_COLUMNS: PartialRecord<
  OrderReportColumnLiterals,
  OrderTableColumnLiterals
> = {
  status: 'status',
  orderId: 'number',
  branch: 'client',
  createdDate: 'dateCreated',
  contactName: 'contactName',
  poNumber: 'poNumber',
  customerTotalTariff: 'actualRevenue',
  vehicleInfo: 'commodity',
  lotNumber: 'lotNumber',
  pickupBusinessName: 'pickupTerminal',
  pickupAddress: 'pickupAddress',
  deliveryAddress: 'deliveryAddress',
  deliveryBusinessName: 'deliveryTerminal',
  pickupRequestedDate: 'requestedPickupDate',
  pickupScheduledDate: 'scheduledPickupDate',
  pickupCompletedDate: 'pickupCompletedDate',
  deliveryRequestedDate: 'requestedDeliveryDate',
  deliveryScheduledDate: 'scheduledDeliveryDate',
  deliveryCompletedDate: 'deliveryCompletedDate',
  vin: 'vin',
  updatedAt: 'dateUpdated',
};

export const ORDER_COLUMNS: GridTableColumnDef<OrderTableFields>[] = [
  {
    header: () => <Typography>Status</Typography>,
    id: 'status',
    accessorKey: 'status',
    enableSorting: true,
    meta: { width: 112 },
    cell: ({ getValue }) => {
      return <OrderJobStatusPill variant="order" status={getValue() as OrderStatusLiterals} />;
    },
  },
  {
    header: () => <Typography>Branch (Client)</Typography>,
    id: 'client',
    accessorKey: 'client',
    enableSorting: false,
    meta: { width: 128 },
    cell: ({ getValue }) => (
      <Typography className="capitalize">
        <Tooltip title={getValue() as string}>{getValue() as string}</Tooltip>
      </Typography>
    ),
  },
  {
    header: () => <Typography disableEllipse>Cost</Typography>,
    id: 'actualRevenue',
    accessorKey: 'actualRevenue',
    enableSorting: true,
    meta: { width: 80 },
    cell: ({ getValue }) => <Typography>{getValue() as string}</Typography>,
  },
  {
    header: () => <Typography>Created Date</Typography>,
    id: 'dateCreated',
    accessorKey: 'dateCreated',
    enableSorting: true,
    meta: { width: 150 },
    cell: ({ getValue }) => {
      return (
        <LabelGroup
          value={formatDateRange(getValue() as string, null, FORMAT_VALUE_DATE)}
          tooltipValue={formatDateRange(getValue() as string, null, FORMAT_TOOLTIP_DATE)}
        />
      );
    },
  },
  {
    header: () => <Typography>PO Number</Typography>,
    id: 'poNumber',
    accessorKey: 'poNumber',
    enableSorting: false,
    meta: { width: 128 },
    cell: ({ getValue }) => (
      <Typography>
        <Tooltip title={getValue() as string}>{getValue() as string}</Tooltip>
      </Typography>
    ),
  },
  {
    header: () => <Typography>Contact Name</Typography>,
    id: 'contactName',
    accessorKey: 'contactName',
    enableSorting: false,
    meta: { width: 128 },
    cell: ({ getValue }) => (
      <Typography className="capitalize">
        <Tooltip title={getValue() as string}>{getValue() as string}</Tooltip>
      </Typography>
    ),
  },
  {
    header: () => <Typography>Commodity</Typography>,
    id: 'commodity',
    accessorKey: 'commodity',
    enableSorting: false,
    meta: { width: 304 },
    cell: ({ getValue, row }) => {
      const commodity = getValue() as string;
      const condition = row.original.condition;
      return (
        <div className="grid items-center gap-4" style={{ gridTemplateColumns: '1rem 1fr auto' }}>
          <div>
            <CommodityCondition operable={condition ?? null} />
          </div>
          <LabelGroup value={commodity} />
        </div>
      );
    },
  },
  {
    header: () => <Typography>VIN</Typography>,
    id: 'vin',
    accessorKey: 'vin',
    enableSorting: false,
    meta: { width: 152 },
    cell: ({ getValue }) => (
      <Typography>
        <Tooltip title={getValue() as string}>{getValue() as string}</Tooltip>
      </Typography>
    ),
  },
  {
    header: () => <Typography>Lot Number</Typography>,
    id: 'lotNumber',
    accessorKey: 'lotNumber',
    enableSorting: false,
    meta: { width: 128 },
    cell: ({ getValue }) => (
      <Typography>
        <Tooltip title={getValue() as string}>{getValue() as string}</Tooltip>
      </Typography>
    ),
  },
  {
    header: () => <Typography>Pickup Terminal</Typography>,
    id: 'pickupTerminal',
    accessorKey: 'pickupTerminal',
    enableSorting: true,
    meta: { width: 160 },
    cell: ({ getValue }) => (
      <Typography>
        <Tooltip title={getValue() as string}>{getValue() as string}</Tooltip>
      </Typography>
    ),
  },
  {
    header: () => <Typography>Pickup Location</Typography>,
    id: 'pickupAddress',
    accessorKey: 'pickupAddress',
    enableSorting: false,
    meta: { width: 160 },
    cell: ({ getValue }) => {
      const value = getValue() as string;

      return (
        <Typography>
          <Tooltip title={value}>{value}</Tooltip>
        </Typography>
      );
    },
  },
  {
    header: () => <Typography>Delivery Terminal</Typography>,
    id: 'deliveryTerminal',
    accessorKey: 'deliveryTerminal',
    enableSorting: true,
    meta: { width: 160 },
    cell: ({ getValue }) => (
      <Typography>
        <Tooltip title={getValue() as string}>{getValue() as string}</Tooltip>
      </Typography>
    ),
  },
  {
    header: () => <Typography>Delivery Location</Typography>,
    id: 'deliveryAddress',
    accessorKey: 'deliveryAddress',
    enableSorting: false,
    meta: { width: 160 },
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return (
        <Typography>
          <Tooltip title={value}>{value}</Tooltip>
        </Typography>
      );
    },
  },
  {
    header: () => <Typography>Requested Pickup Date</Typography>,
    id: 'requestedPickupDate',
    accessorKey: 'requestedPickupDate',
    enableSorting: true,
    meta: { width: 250 },
    cell: ({ getValue }) => {
      const value = getValue() as DateRange;

      return (
        <LabelGroup
          value={formatDateRange(value.start, value.end ?? null, FORMAT_VALUE_DATE)}
          tooltipValue={formatDateRange(value.start, value.end ?? null, FORMAT_TOOLTIP_DATE)}
        />
      );
    },
  },
  {
    header: () => <Typography>Scheduled Pickup Date</Typography>,
    id: 'scheduledPickupDate',
    accessorKey: 'scheduledPickupDate',
    enableSorting: true,
    meta: { width: 250 },
    cell: ({ getValue }) => {
      const value = getValue() as DateRange;
      const date = formatDateRange(value.start, value.end ?? null, FORMAT_TOOLTIP_DATE);

      return (
        <LabelGroup
          value={formatDateRange(value.start, value.end ?? null, FORMAT_VALUE_DATE)}
          tooltipValue={date}
        />
      );
    },
  },
  {
    header: () => <Typography>Picked Up On</Typography>,
    id: 'pickupCompletedDate',
    accessorKey: 'pickupCompletedDate',
    enableSorting: false,
    meta: { width: 192 },
    cell: ({ getValue }) => {
      const date = formatDateRange(getValue() as ISODate, null, FORMAT_TOOLTIP_DATE);

      return (
        <LabelGroup
          value={formatDateRange(getValue() as ISODate, null, FORMAT_VALUE_DATE)}
          tooltipValue={date}
        />
      );
    },
  },
  {
    header: () => <Typography>Requested Delivery Date</Typography>,
    id: 'requestedDeliveryDate',
    accessorKey: 'requestedDeliveryDate',
    enableSorting: true,
    meta: { width: 250 },
    cell: ({ getValue }) => {
      const value = getValue() as DateRange;
      const date = formatDateRange(value.start, value.end ?? null, FORMAT_TOOLTIP_DATE);

      return (
        <LabelGroup
          value={formatDateRange(value.start, value.end ?? null, FORMAT_VALUE_DATE)}
          tooltipValue={date}
        />
      );
    },
  },
  {
    header: () => <Typography>Scheduled Delivery Date</Typography>,
    id: 'scheduledDeliveryDate',
    accessorKey: 'scheduledDeliveryDate',
    enableSorting: true,
    meta: { width: 250 },
    cell: ({ getValue }) => {
      const value = getValue() as DateRange;
      const date = formatDateRange(value.start, value.end ?? null, FORMAT_TOOLTIP_DATE);

      return (
        <LabelGroup
          value={formatDateRange(value.start, value.end ?? null, FORMAT_VALUE_DATE)}
          tooltipValue={date}
        />
      );
    },
  },
  {
    header: () => <Typography>Delivered On</Typography>,
    id: 'deliveryCompletedDate',
    accessorKey: 'deliveryCompletedDate',
    enableSorting: false,
    meta: { width: 192 },
    cell: ({ getValue }) => {
      return (
        <LabelGroup
          value={formatDateRange(getValue() as ISODate, null, FORMAT_VALUE_DATE)}
          tooltipValue={formatDateRange(getValue() as ISODate, null, FORMAT_TOOLTIP_DATE)}
        />
      );
    },
  },
  {
    header: () => <Typography>Updated Date</Typography>,
    id: 'dateUpdated',
    accessorKey: 'dateUpdated',
    enableSorting: true,
    meta: { width: 150 },
    cell: ({ getValue }) => {
      return (
        <LabelGroup
          value={formatDateRange(getValue() as ISODate, null, FORMAT_VALUE_DATE)}
          tooltipValue={formatDateRange(getValue() as ISODate, null, FORMAT_TOOLTIP_DATE)}
        />
      );
    },
  },
];

export const SORTABLE_FIELD_MAP: Record<SORTABLE_FIELD_LITERALS, GetOrdersSortableFieldLiterals> = {
  number: 'number',
  status: 'status',
  dateCreated: 'date_created',
  actualRevenue: 'total_expense',
  pickupTerminal: 'origin_name',
  deliveryTerminal: 'destination_name',
  requestedPickupDate: 'requested_pickup_date',
  scheduledPickupDate: 'scheduled_pickup_date',
  requestedDeliveryDate: 'requested_delivery_date',
  scheduledDeliveryDate: 'scheduled_delivery_date',
  dateUpdated: 'date_updated',
};
